import { Link } from "@tanstack/react-router";
import { ArrowRight, FileText, Mic, Music, Paperclip, Square, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import type { CreditStatus } from "@/hooks/useCreditBalance";
import type { PendingAttachments } from "@/hooks/usePendingAttachments";
import { cn } from "@/lib/utils";

interface ChatInputProps {
  attachments: PendingAttachments;
  creditStatus?: CreditStatus;
  disabled?: boolean;
  onSend: (args: { content: string; files?: File[] }) => void;
  /** Prefill signal from a starter template — nonce re-triggers on re-pick. */
  draft?: { text: string; nonce: number } | null;
}

export function ChatInput({
  attachments,
  creditStatus = "ready",
  disabled,
  onSend,
  draft,
}: ChatInputProps) {
  const [input, setInput] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState("");
  const [recordingSecs, setRecordingSecs] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setIsRecording(false);
  }, []);

  async function startRecording() {
    if (disabled) return;
    setRecordingError("");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioChunksRef.current = [];

      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        if (blob.size === 0) return;

        const file = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
        // Auto-send the voice memo immediately
        onSend({ content: "", files: [file] });
      };

      recorder.start();
      setIsRecording(true);
    } catch (err) {
      const msg = err instanceof DOMException && err.name === "NotAllowedError"
        ? "Microphone access denied. Check browser permissions."
        : "Could not start recording.";
      setRecordingError(msg);
      console.warn("[voice]", msg, err);
      // Clear error after 3s
      setTimeout(() => setRecordingError(""), 3000);
    }
  }

  function toggleRecording() {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  // Prefill from a picked starter template — fills the composer, focuses it,
  // and grows the textarea, so the user can tweak the [slots] and send.
  useEffect(() => {
    if (!draft) return;
    setInput(draft.text);
    const el = textareaRef.current;
    if (el) {
      el.focus();
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 208)}px`;
      el.setSelectionRange(el.value.length, el.value.length);
    }
    // draft is a fresh object per pick (new nonce), so this fires exactly
    // once per template selection — including re-picking the same one.
  }, [draft]);

  // Recording timer — auto-stop at 2 minutes
  useEffect(() => {
    if (!isRecording) {
      setRecordingSecs(0);
      return;
    }
    const id = setInterval(() => {
      setRecordingSecs((s) => {
        if (s + 1 >= 120) {
          stopRecording();
          return 0;
        }
        return s + 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [isRecording, stopRecording]);

  function handleSend() {
    const trimmed = input.trim();
    if (disabled || (!trimmed && attachments.files.length === 0)) return;
    onSend({
      content: trimmed,
      files: attachments.files.length ? attachments.files : undefined,
    });
    setInput("");
    attachments.clear();
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    if (disabled) return;
    attachments.addFiles(Array.from(e.target.files ?? []));
    e.target.value = "";
  }

  const canSend =
    !disabled && creditStatus === "ready" && (input.trim() || attachments.files.length > 0);

  const outOfCredits = creditStatus === "empty" || creditStatus === "negative";

  const placeholder = disabled
    ? "Waiting for agent..."
    : creditStatus === "loading"
      ? "Loading credits…"
      : outOfCredits
        ? "Out of credits — top up to send"
        : "Ask anything…";

  const iconButton =
    "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-black/40 transition-colors hover:bg-black/5 hover:text-black/70 disabled:opacity-30 disabled:hover:bg-transparent dark:text-white/40 dark:hover:bg-white/10 dark:hover:text-white/70";

  return (
    <div className="border-t border-black/5 bg-white/50 px-3 py-4 dark:border-white/10 dark:bg-transparent md:px-4">
      {attachments.previews.length > 0 && (
        <div className="mb-2 flex gap-2 overflow-x-auto px-1">
          {attachments.previews.map((preview, i) => (
            <div key={i} className="group relative shrink-0">
              {preview.startsWith("file:") ? (
                <div className="flex h-16 w-16 flex-col items-center justify-center rounded-lg border border-black/10 bg-black/5 dark:border-white/10 dark:bg-white/5">
                  {attachments.files[i]?.type === "application/pdf" ? (
                    <FileText className="h-5 w-5 text-black/40 dark:text-white/40" />
                  ) : (
                    <Music className="h-5 w-5 text-black/40 dark:text-white/40" />
                  )}
                  <span className="mt-0.5 max-w-[56px] truncate text-[9px] text-black/40 dark:text-white/40">
                    {attachments.files[i]?.name}
                  </span>
                </div>
              ) : (
                <img
                  src={preview}
                  alt={attachments.files[i]?.name ?? "attachment"}
                  className="h-16 w-16 rounded-lg border border-black/10 object-cover dark:border-white/10"
                />
              )}
              <button
                onClick={() => attachments.removeFile(i)}
                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white opacity-0 transition-opacity group-hover:opacity-100"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Two-row composer — the field gets its own line so it can breathe;
          controls live on a calmer row below (Perplexity-style). */}
      <div className="flex flex-col gap-2 rounded-[26px] border border-black/5 bg-white/85 px-2.5 py-2.5 shadow-sm backdrop-blur-sm transition-all focus-within:border-brand-coral-dark/50 focus-within:ring-1 focus-within:ring-brand-coral-dark/15 dark:border-white/10 dark:bg-white/5 dark:backdrop-blur-xl">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf,audio/webm,audio/mpeg,.mp3"
          multiple
          disabled={disabled}
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Row 1 — the field */}
        {isRecording ? (
          <div className="flex items-center gap-2.5 px-2 py-1.5">
            <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-brand-coral" />
            <span className="text-sm font-semibold tabular-nums text-brand-coral">
              {String(Math.floor(recordingSecs / 60)).padStart(2, "0")}:{String(recordingSecs % 60).padStart(2, "0")}
            </span>
            <span className="text-sm text-black/40 dark:text-white/40">Recording…</span>
          </div>
        ) : (
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            disabled={disabled || creditStatus !== "ready"}
            placeholder={placeholder}
            rows={1}
            className="max-h-52 w-full resize-none border-none bg-transparent px-2 pt-1.5 text-base font-medium text-black outline-none focus:outline-none focus-visible:outline-none! placeholder-black/30 disabled:opacity-50 dark:text-white dark:placeholder-white/30"
          />
        )}

        {/* Row 2 — controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className={iconButton}
            title="Attach file"
            aria-label="Attach file"
          >
            <Paperclip className="h-[18px] w-[18px]" />
          </button>

          <div className="ml-auto flex items-center gap-1.5">
            {outOfCredits && !isRecording && (
              <Link
                to="/dashboard"
                className="flex h-9 shrink-0 items-center rounded-full bg-home-yellow px-4 text-xs font-bold uppercase tracking-wider text-home-ink transition-all duration-200 hover:-translate-y-px hover:brightness-95"
              >
                Top up
              </Link>
            )}
            <button
              onClick={toggleRecording}
              disabled={disabled}
              className={cn(
                iconButton,
                isRecording && "animate-pulse bg-brand-coral text-white hover:bg-brand-coral hover:text-white",
                recordingError && !isRecording && "text-brand-coral",
              )}
              title={isRecording ? "Stop recording" : recordingError || "Voice dictation"}
              aria-label={isRecording ? "Stop recording" : "Voice dictation"}
            >
              {isRecording ? <Square className="h-3.5 w-3.5" /> : <Mic className="h-[18px] w-[18px]" />}
            </button>
            <button
              onClick={handleSend}
              disabled={!canSend}
              aria-label="Send"
              className={cn(
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all",
                canSend
                  ? "bg-home-yellow text-home-ink shadow-md hover:scale-105"
                  : "bg-black/5 text-black/20 dark:bg-white/10 dark:text-white/20",
              )}
            >
              <ArrowRight className="h-[18px] w-[18px]" strokeWidth={2.4} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
